import React from "react";
import styled from "styled-components";
import { Button } from "@cinesys/ui/components";
import { RunRecord } from "./mockData";
import { RunTone, runDisplay } from "./service";
import { KeyGrid, Mono } from "./ui";

/**
 * Bounded, read-only detail for a single recorded run. Sourced entirely from the persisted run
 * summary — counts, timestamps, config/plan fingerprints, and a bounded per-asset failure list.
 * Deliberately NOT a log store: no raw source rows, metadata payloads, HTTP bodies, or tokens.
 * (Warning MESSAGE text is not persisted today — only the count — so the view shows the count and
 * says so, rather than inventing detail.)
 */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 55;
`;
const Card = styled.div`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 620px;
  max-height: 86vh;
  overflow: auto;
  padding: 22px 24px;
`;
const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;
const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #fff;
`;
const StatusCell = styled.span<{ $tone: string }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: ${(p) => p.$tone};
  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.$tone};
  }
`;
const SubHead = styled.div`
  margin: 18px 0 8px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--miInkFaint);
`;
const Fail = styled.div`
  border: 1px solid var(--miLine);
  border-left: 3px solid var(--miDanger);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  font-size: 13px;
  color: var(--miInkSoft);

  .t {
    color: #fff;
    font-weight: 500;
  }
  .e {
    margin-top: 3px;
    color: var(--miInkFaint);
  }
`;
const Fine = styled.p`
  margin: 8px 0 0;
  font-size: 12.5px;
  color: var(--miInkFaint);
`;
const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 22px;
`;

const TONE_VAR: Record<RunTone, string> = {
  ok: "var(--miOk)",
  warn: "var(--miWarn)",
  danger: "var(--miDanger)",
  running: "var(--miAccentBright)",
  info: "var(--miInkSoft)",
};

export default function RunDetailModal({
  run,
  onClose,
}: {
  run: RunRecord;
  onClose: () => void;
}) {
  const failures = run.failureSummary ?? [];
  const CAP = 50;
  const display = runDisplay({
    status: run.status,
    outcome: run.outcome,
    changed: run.changed,
  });
  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Head>
          <div>
            <Title>Run detail</Title>
            <Mono>{run.id}</Mono>
          </div>
          <StatusCell $tone={TONE_VAR[display.tone]}>
            {display.label}
          </StatusCell>
        </Head>

        {run.outcome === "stale" && (
          <Fine>
            The reviewed plan no longer matched current state, so this run wrote
            nothing. Re-open the workflow, review the fresh plan, and run again.
          </Fine>
        )}
        {run.outcome === "planned" && (
          <Fine>
            Reconciled successfully, but writes are disabled — no metadata was
            written. The counts below are what would change once writes are
            enabled.
          </Fine>
        )}

        <KeyGrid>
          <dt>Trigger</dt>
          <dd>{run.trigger}</dd>
          {run.startedAt && (
            <>
              <dt>Started</dt>
              <dd>{run.startedAt}</dd>
            </>
          )}
          <dt>Completed</dt>
          <dd>{run.date}</dd>
          <dt>Assets targeted</dt>
          <dd>{run.total}</dd>
          <dt>Changed</dt>
          <dd>{run.changed}</dd>
          <dt>Unchanged</dt>
          <dd>{run.unchanged}</dd>
          <dt>Warnings</dt>
          <dd>{run.warnings}</dd>
          <dt>Failures</dt>
          <dd style={{ color: run.failures ? "var(--miDanger)" : undefined }}>
            {run.failures}
          </dd>
          {run.revision && (
            <>
              <dt>Revision</dt>
              <dd>
                <Mono>{run.revision}</Mono>
              </dd>
            </>
          )}
          {run.planRevision && (
            <>
              <dt>Plan revision</dt>
              <dd>
                <Mono>{run.planRevision}</Mono>
              </dd>
            </>
          )}
        </KeyGrid>

        {run.warnings > 0 && failures.length === 0 && (
          <Fine>
            {run.warnings} warning{run.warnings === 1 ? "" : "s"} were recorded
            for this run. Per-message detail is not retained.
          </Fine>
        )}

        {failures.length > 0 && (
          <>
            <SubHead>Failures · {run.failures}</SubHead>
            {failures.slice(0, CAP).map((f) => (
              <Fail key={f.assetId}>
                <div className="t">{f.assetTitle}</div>
                <div className="e">{f.error}</div>
                <Mono>{f.assetId}</Mono>
              </Fail>
            ))}
            {failures.length > CAP && (
              <Fine>
                Showing the first {CAP} of {failures.length} recorded failures.
              </Fine>
            )}
          </>
        )}

        <Foot>
          <Button label="Close" type="default" onClick={onClose} />
        </Foot>
      </Card>
    </Overlay>
  );
}
